/**
 * Dashboard Page
 *
 * Shows overview stats and quick actions.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  ProgressBar,
  BlockStack,
  InlineStack,
  Button,
  Banner,
  SkeletonBodyText,
  Icon,
} from '@shopify/polaris';
import { ProductIcon, WandIcon, CheckIcon } from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../providers/ApiProvider';
import type { DashboardStats } from '../types';

export function DashboardPage() {
  const navigate = useNavigate();
  const api = useApi();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadStats = useCallback(async () => {
    try {
      setLoading(true);
      const data = await api.getDashboardStats();
      setStats(data);
      setError(null);
    } catch (err) {
      setError('Fehler beim Laden der Statistiken');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Card>
              <SkeletonBodyText lines={4} />
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Dashboard">
        <Layout>
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  const usagePercent = stats ? Math.round((stats.optimizedProducts / stats.productsLimit) * 100) : 0;

  return (
    <Page title="Dashboard">
      <Layout>
        {/* Usage Banner */}
        {usagePercent >= 80 && (
          <Layout.Section>
            <Banner
              title="Kontingent fast aufgebraucht"
              tone="warning"
              action={{ content: 'Upgrade', onAction: () => navigate('/settings') }}
            >
              Du hast {usagePercent}% deines monatlichen Kontingents verbraucht.
            </Banner>
          </Layout.Section>
        )}

        {/* Stats Cards */}
        <Layout.Section>
          <InlineStack gap="400" wrap={false}>
            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="h3" variant="headingMd">Produkte gesamt</Text>
                  <Icon source={ProductIcon} tone="base" />
                </InlineStack>
                <Text as="p" variant="heading2xl">{stats?.totalProducts || 0}</Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="h3" variant="headingMd">Optimiert</Text>
                  <Icon source={WandIcon} tone="success" />
                </InlineStack>
                <Text as="p" variant="heading2xl">{stats?.optimizedProducts || 0}</Text>
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="200">
                <InlineStack align="space-between">
                  <Text as="h3" variant="headingMd">Angewendet</Text>
                  <Icon source={CheckIcon} tone="success" />
                </InlineStack>
                <Text as="p" variant="heading2xl">{stats?.appliedProducts || 0}</Text>
              </BlockStack>
            </Card>
          </InlineStack>
        </Layout.Section>

        {/* Usage Progress */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h3" variant="headingMd">Monatliches Kontingent</Text>
                <Text as="span" variant="bodyMd" tone="subdued">
                  {stats?.optimizedProducts || 0} / {stats?.productsLimit || 0} Produkte
                </Text>
              </InlineStack>
              <ProgressBar progress={usagePercent} size="small" tone={usagePercent >= 80 ? 'critical' : 'primary'} />
              <Text as="p" variant="bodySm" tone="subdued">
                Plan: {stats?.plan || 'Free'} ({stats?.productsLimit || 0} Produkte/Monat)
              </Text>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* Quick Actions */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              <Text as="h3" variant="headingMd">Schnellaktionen</Text>
              <InlineStack gap="300">
                <Button onClick={() => navigate('/products')}>
                  Produkte anzeigen
                </Button>
                <Button variant="primary" onClick={() => navigate('/optimize')}>
                  Optimierung starten
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
